/**
 * Client-side image processing for showcase uploads.
 *
 * Pipeline per PRD § Phase 3:
 *
 *   1. Accept JPEG / PNG / HEIC / WEBP up to 25 MB.
 *   2. HEIC → JPEG conversion in the browser (heic2any).
 *   3. Downscale so long edge ≤ 4096.
 *   4. Strip EXIF (a canvas re-encode drops it — canvas has no EXIF concept).
 *   5. Produce a thumbnail (~600 px long edge).
 *
 * Returns a `{full: Blob, fullMeta, thumb: Blob, thumbMeta}` bundle
 * ready for direct PUT to R2 via presigned URLs.
 */

const MAX_LONG_EDGE = 4096;
const THUMB_LONG_EDGE = 600;
const IMAGE_QUALITY = 0.9;
const THUMB_QUALITY = 0.82;

export async function processImage(file) {
  let workingFile = file;

  // HEIC → JPEG (heic2any is a heavy library; lazy import so it's only
  // pulled into the bundle when the user actually picks a HEIC file).
  const isHeic =
    /image\/hei[cf]/i.test(file.type) || /\.(heic|heif)$/i.test(file.name || "");
  if (isHeic) {
    const heic2any = (await import("heic2any")).default;
    const converted = await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: 0.9,
    });
    workingFile = new File([converted], (file.name || "photo") + ".jpg", {
      type: "image/jpeg",
    });
  }

  const bitmap = await createBitmap(workingFile);

  const full = await encodeAt(bitmap, MAX_LONG_EDGE, IMAGE_QUALITY);
  const thumb = await encodeAt(bitmap, THUMB_LONG_EDGE, THUMB_QUALITY);
  bitmap.close?.();

  return { full, thumb };
}

async function createBitmap(blob) {
  if ("createImageBitmap" in window) {
    try {
      return await createImageBitmap(blob);
    } catch (err) {
      // createImageBitmap can throw on some HEIC/EXIF-rotated payloads
      // even after the heic2any pass; fall back to <img> decode below.
      console.warn(
        "[imageProcessing] createImageBitmap failed, falling back to Image decode:",
        err && err.message ? err.message : err,
      );
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    await img.decode();
    return img;
  } finally {
    // Note: don't revoke immediately — the caller may still draw the image;
    // GC will handle it once the bitmap/image is done.
  }
}

async function encodeAt(bitmap, longEdge, quality) {
  const [w, h] = bitmap.width && bitmap.height
    ? [bitmap.width, bitmap.height]
    : [bitmap.naturalWidth, bitmap.naturalHeight];
  const long = Math.max(w, h);
  const scale = long > longEdge ? longEdge / long : 1;
  const outW = Math.round(w * scale);
  const outH = Math.round(h * scale);

  const canvas =
    "OffscreenCanvas" in window
      ? new OffscreenCanvas(outW, outH)
      : Object.assign(document.createElement("canvas"), { width: outW, height: outH });
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, outW, outH);

  const blob = await ("convertToBlob" in canvas
    ? canvas.convertToBlob({ type: "image/jpeg", quality })
    : new Promise((res) =>
        canvas.toBlob((b) => res(b), "image/jpeg", quality),
      ));
  return { blob, width: outW, height: outH };
}
